import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Pencil, Cake } from 'lucide-react';
import { EditDetail } from './EditDetail';
import { CreateContact } from './CreateContact';
import { TableOfContents } from './FieldComponents';

export const LeadContact = ({ contact }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [createContact, setCreateContact] = useState(false);

    if (!contact) {
        return (
            <div className="case-section lead-contact">
                <h2>Contact Information</h2>
                <div className="no-contact">
                    <p>No contact information available for this lead.</p>
                </div>
            </div>
        );
    }

    const handleEdit = () => {
        setShowEditModal(true);
    };

    const handleNewContactCreated = (newContact) => {
        setCreateContact(false);
        
        // If there's a callback stored for this contact creation, call it
        if (window.contactCreationCallback && typeof window.contactCreationCallback === 'function') {
            window.contactCreationCallback(newContact);
            window.contactCreationCallback = null; // Clear the callback
        }
    };

    const formatAddress = (addr) => {
        return encodeURIComponent(
            [addr.line1, addr.line2, addr.city, addr.state]
            .filter(Boolean)
            .join(', ')
        );
    };

    const addressQuery = formatAddress(contact.addresses?.[0] || {});
    const formattedDob = contact?.date_of_birth ? new Date(contact.date_of_birth).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }) : null;

    return (
        <div className="case-section lead-contact-section">
            <div className='toc-header'>
                <h3>Contact Information</h3>
            </div>
            <div className="contact-card">
                <div className="contact-header">
                    <div className="lead-contact">
                        <div className='lead-contact-identity'>
                            {contact?.profile_picture ? (
                                <img src={`https://api.casedb.co/${contact?.profile_picture}`} alt="Profile"  className='contact-initials' />
                            ) : (
                                <div className="contact-initials">
                                    {(contact?.first_name?.charAt(0) || '') + (contact?.last_name?.charAt(0) || '')}
                                </div>
                            )}
                            <div className='lead-contact-title-block'>
                                <h3 className='lead-contact-name' onClick={handleEdit}>{contact?.full_name}</h3>
                                {formattedDob && (
                                    <div className="contact-item lead-contact-dob subtext">
                                        <Cake size={18} className='subtext' />
                                        {formattedDob}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button type='button' className='action-micro tertiary lead-contact-edit' onClick={handleEdit} title='Edit contact'>
                            <Pencil size={16} />
                        </button>
                    </div>
                </div>

                {contact?.phones.length > 0 || contact?.emails.length > 0 || contact?.addresses.length > 0 ? (
                    <div className="contact-details">
                        {contact?.phones && contact?.phones?.length > 0 && (
                            <div className="contact-section">
                                    <h4>Phone Numbers</h4>
                                    {contact?.phones?.map((phone, index) => (
                                        <div key={index} className="contact-item lead-contact-detail-row">
                                        <Phone size={18} className='subtext' />
                                        <>
                                            <a href={`tel:${phone?.number}`}>{phone?.number}</a>
                                            {phone?.type && <span className="detail-type">{phone?.type}</span>}
                                        </>
                                    </div>
                                ))}
                            </div>
                        )}

                        {contact?.emails && contact?.emails?.length > 0 && (
                            <div className="contact-section">
                                <h4>Email Addresses</h4>
                                {contact?.emails?.map((email, index) => (
                                    <div key={index} className="contact-item lead-contact-detail-row">
                                        <Mail size={18} className='subtext' />
                                        <>
                                            <a href={`mailto:${email?.email}`}>{email?.email}</a>
                                            {email?.type && <span className="detail-type">{email?.type}</span>}
                                        </>
                                    </div>
                                ))}
                            </div>
                        )}

                        {contact?.addresses && addressQuery && contact?.addresses?.length > 0 && (
                            <div className="contact-section">
                                <h4>Addresses</h4>
                                {contact.addresses.map((address, index) => {
                                    const line1 = address?.line1?.trim();
                                    const line2 = address?.line2?.trim();
                                    const city = address?.city?.trim();
                                    const state = address?.state?.trim();
                                    const zip = (address?.zip || address?.postal_code)?.trim();
                                    
                                    const fullAddress = [
                                        line1,
                                        line2,
                                        city,
                                        state,
                                        zip,
                                    ]
                                        .filter(Boolean)
                                        .reduce((acc, curr, idx) => {
                                            if (idx === 0) return curr;
                                            if (idx === 4 && line2.length > 0) {
                                                return acc + ' ' + curr;
                                            } else if (idx === 3 && curr === zip) {
                                                return acc + ' ' + curr;
                                            }
                                            return acc + ', ' + curr;
                                        }, '');

                                    return (
                                        <div key={index} className="contact-item lead-contact-detail-row subtext">
                                            <MapPin size={18} />
                                            <>{fullAddress}</>
                                            {address?.type && (
                                                <span className="detail-type">{address.type.trim()}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="no-contact-details">
                        <p>No contact details available.</p>
                    </div>
                )}
            </div>
            

            <div className='map-embed-container'>
                <iframe
                    className='map-embed'
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBIJcqTSLaIqtZWQSTPHGJ_IvX1gkn2q08&q=${addressQuery}`}
                ></iframe>
            </div>

            {showEditModal && (
                <EditDetail
                    contactData={contact}
                    setEditContact={() => setShowEditModal(false)}
                    setCreateContact={setCreateContact}
                />
            )}
            {createContact && (
                <CreateContact 
                    setCreateContact={setCreateContact} 
                    onContactCreated={handleNewContactCreated} 
                />
            )}
        </div>
    );
};
